import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiUploadFile } from "@/lib/api";
import type {
  DisponibilitaResponse,
  FabbisognoResponse,
  FeedbackBody,
  ImpostazioniResponse,
  LineaRead,
  ParametriPreset,
  PianoResponse,
  PropostaResponse,
  RegolaRead,
  RiconciliazioneResponse,
  RicettaFamigliaRead,
  SetupRead,
  StampoRead,
  TransizioneRead,
  VincoloLineaRead,
} from "@/types/m2";

// ── Query ────────────────────────────────────────────────────────────

export function useM2Piano() {
  return useQuery({
    queryKey: ["m2", "piano"],
    queryFn: () => apiFetch<PianoResponse>("/api/v1/m2/piano"),
    staleTime: 30_000,
  });
}

export function useM2Impostazioni() {
  return useQuery({
    queryKey: ["m2", "impostazioni"],
    queryFn: () => apiFetch<ImpostazioniResponse>("/api/v1/m2/impostazioni"),
    staleTime: 30_000,
  });
}

export function useM2Proposta() {
  return useQuery({
    queryKey: ["m2", "proposta"],
    queryFn: () => apiFetch<PropostaResponse | null>("/api/v1/m2/proposta"),
    staleTime: 30_000,
  });
}

export function useM2Disponibilita() {
  return useQuery({
    queryKey: ["m2", "disponibilita"],
    queryFn: () => apiFetch<DisponibilitaResponse>("/api/v1/m2/disponibilita"),
    staleTime: 30_000,
  });
}

export function useM2Fabbisogno() {
  return useQuery({
    queryKey: ["m2", "fabbisogno"],
    queryFn: () => apiFetch<FabbisognoResponse>("/api/v1/m2/fabbisogno"),
    staleTime: 30_000,
  });
}

export function useM2Riconciliazione() {
  return useQuery({
    queryKey: ["m2", "riconciliazione"],
    queryFn: () => apiFetch<RiconciliazioneResponse>("/api/v1/m2/riconciliazione"),
    staleTime: 30_000,
  });
}

// ── Mutation ─────────────────────────────────────────────────────────

function jsonInit(method: string, body?: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  };
}

export function useM2Ottimizza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<PropostaResponse>("/api/v1/m2/ottimizza", jsonInit("POST")),
    onSuccess: (data) => qc.setQueryData(["m2", "proposta"], data),
  });
}

export function useM2Feedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FeedbackBody) =>
      apiFetch("/api/v1/m2/feedback", jsonInit("POST", body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "proposta"] }),
  });
}

export function useM2Reimporta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<PianoResponse>("/api/v1/m2/reimporta", jsonInit("POST")),
    onSuccess: (data) => {
      qc.setQueryData(["m2", "piano"], data);
      qc.invalidateQueries({ queryKey: ["m2", "proposta"] });
      qc.invalidateQueries({ queryKey: ["m2", "disponibilita"] });
    },
  });
}

/**
 * Carica il piano MES vero: i due export (estrusione + confezionamento).
 *
 * È il gesto che «Reimporta» non poteva fare — quello rilegge i file congelati
 * nell'immagine, ed è la ragione per cui il piano in produzione è rimasto al 29
 * aprile. Dal 27-08 l'endpoint sta fuori dai moduli (`/dati/piano-mes`): chi
 * carica i file non ha `modulo.M2`, e finché stava sotto M2 non poteva arrivarci. Volutamente **non** invalida la disponibilità: il calcolo delle date
 * verso M3 è un gesto a parte (vedi `useM2RicalcolaDisponibilita`).
 */
export function useM2CaricaPiano() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (files: { estrusione: File; confezionamento: File }) => {
      const body = new FormData();
      body.append("estrusione", files.estrusione);
      body.append("confezionamento", files.confezionamento);
      // `apiUploadFile` e non `apiFetch`: quest'ultimo forza
      // `Content-Type: application/json` e il boundary del multipart si perde.
      return apiUploadFile<PianoResponse>("/api/v1/dati/piano-mes", body);
    },
    onSuccess: (data) => {
      qc.setQueryData(["m2", "piano"], data);
      qc.invalidateQueries({ queryKey: ["m2", "proposta"] });
      qc.invalidateQueries({ queryKey: ["m2", "fabbisogno"] });
      // Il piano ha un semaforo nel riquadro «Stato dati»: senza questa riga
      // resterebbe rosso fino al prossimo giro di cache.
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useM2RicalcolaDisponibilita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<DisponibilitaResponse>("/api/v1/m2/ricalcola-disponibilita", jsonInit("POST")),
    onSuccess: (data) => qc.setQueryData(["m2", "disponibilita"], data),
  });
}

export function useM2PatchLinea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<LineaRead> }) =>
      apiFetch<LineaRead>(`/api/v1/m2/linee/${id}`, jsonInit("PATCH", body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}

export function useM2PutSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, minuti }: { id: string; minuti: number }) =>
      apiFetch<SetupRead>(`/api/v1/m2/setup/${id}`, jsonInit("PUT", { minuti })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}

export function useM2PutParametri() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      preset_nome: string;
      peso_puntualita?: number;
      peso_cambi_stampo?: number;
      peso_anticipo?: number;
      peso_saturazione?: number;
      finestra_giorni?: number;
      minuti_cambio_stampo?: number;
      rampa_minuti?: number;
    }) => apiFetch<ParametriPreset[]>("/api/v1/m2/parametri", jsonInit("PUT", body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}

export function useM2DeleteRegola() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/v1/m2/regole/${id}`, jsonInit("DELETE")),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}

export function useM2CreaRegola() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<RegolaRead>) =>
      apiFetch<RegolaRead>("/api/v1/m2/regole", jsonInit("POST", body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}

export function useM2PatchStampo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<StampoRead> }) =>
      apiFetch<StampoRead>(`/api/v1/m2/stampi/${id}`, jsonInit("PATCH", body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}

export function useM2PutTransizione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, minuti }: { id: string; minuti: number }) =>
      apiFetch<TransizioneRead>(`/api/v1/m2/transizioni/${id}`, jsonInit("PUT", { minuti })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}

export function useM2PatchVincolo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<VincoloLineaRead> }) =>
      apiFetch<VincoloLineaRead>(`/api/v1/m2/vincoli/${id}`, jsonInit("PATCH", body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}

export function useM2PatchFamiglia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<RicettaFamigliaRead> }) =>
      apiFetch<RicettaFamigliaRead>(`/api/v1/m2/ricette-famiglie/${id}`, jsonInit("PATCH", body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m2", "impostazioni"] }),
  });
}
